import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    styled,
    Divider,
    Box,
    colors
} from '@mui/material';


// Styled components
const StyledTableContainer = styled(TableContainer)({
    width: '100%',
    overflowX: 'auto',
    marginBottom: '24px',
    fontFamily: 'inherit'
});

const StickyCell = styled(TableCell)({
    position: 'sticky',
    left: 0,
    zIndex: 10,
    backgroundColor: 'white'
});

const StickyHeaderCell = styled(TableCell)({
    position: 'sticky',
    left: 0,
    zIndex: 10,
    backgroundColor: 'white'
});

const GroupHeaderCell = styled(TableCell)({
    backgroundColor: '#f5f5f5',
    fontStyle: 'italic',
    position: 'sticky',
    left: 0,
    zIndex: 10
});

const ArrowIcon = styled('span')({
    display: 'inline-block',
    position: 'relative',
    height: 0,
    width: 0,
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    borderRight: '8px solid #34457d',
    marginLeft: '4px'
});

const FinancialTable = ({ data }) => {
    console.log("FinancialTable PROPS DATA :: ", data);
    const dates = data && Object.keys(data?.o_main_file_path)?.sort((a, b) => new Date(b) - new Date(a)) || [];

    const sectionOrder = ["Income Statement", "Balance Sheet", "Cash Flow"];

    // const rowGroups = Object.keys(data)
    //     .filter(key => !['o_main_file_path', 'o_main_file_name'].includes(key))
    //     .map(groupName => ({
    //         name: groupName,
    //         metrics: Object.keys(data[groupName])
    //     }));

    const rowGroups = data && sectionOrder
        .filter(key => data[key]) // Only include sections that exist in the data
        .map(groupName => ({
            name: groupName,
            metrics: Object.keys(data[groupName])
        }));

    const formatValue = (value, isMonetary) => {
        if (value === null || value === undefined) return '-';

        // Try to parse as number
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;

        if (isNaN(num)) return value; // Return as-is if not a number

        if (isMonetary) {
            // Handle negative values properly and divide by 1000
            const isNegative = num < 0;
            const absoluteNum = Math.abs(num) / 1000; // Divide by 1000 for thousands

            return `${isNegative ? '-' : ''}$${absoluteNum.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            })}`;
        } else {
            // Format as plain number with thousand separators
            // For percentages (small numbers), show 2 decimal places
            if (Math.abs(num) < 10 && num !== 0) {
                return num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
            return num.toLocaleString('en-US');
        }
    };

    function formatDate(dateString) {
        // Split the input date by slashes
        // const [day, month, year] = dateString.split('-');
        const [year, month, day] = dateString.split('-'); // Split by hyphens, month, year] = dateString.split('-');

        // Create a Date object (months are 0-indexed in JavaScript)
        const date = new Date(year, month - 1, day);

        // Format options for toLocaleDateString
        const options = {
            month: 'short',  // "Feb"
            day: 'numeric',  // "24"
            year: 'numeric'  // "2025"
        };

        // Return formatted date
        return date.toLocaleDateString('en-US', options);
    }

    if (!data)
        return null

    return (
        <>
            <Box width="100%" flexDirection={"column"} justifyContent="flex-start" mb={1}>
            </Box>
            <Box textAlign={'right'}><small><i>Dollars in Thousands</i></small></Box>
            <StyledTableContainer component={Paper}>
                <Table sx={{ minWidth: '100%' }} size="small">
                    <colgroup>
                        <col style={{ width: '40%' }} />
                        {dates.map((_, index) => (
                            <col key={index} style={{ width: 'auto' }} />
                        ))}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <StickyHeaderCell></StickyHeaderCell>
                            {dates.map((date) => (
                                <TableCell key={date} align="right" sx={{ minWidth: 120, fontWeight: 'bold' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <div>
                                            {/* {new Date(date).toLocaleDateString()} */}
                                            {formatDate(date)}
                                            {data.o_main_file_path[date] && (
                                                <a
                                                    href={data.o_main_file_path[date]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#1976d2', textDecoration: 'underline', marginLeft: '4px' }}
                                                >
                                                    <ArrowIcon />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowGroups.map((group) => (
                            <React.Fragment key={group.name}>
                                <TableRow>
                                    <GroupHeaderCell colSpan={dates.length + 1}>
                                        <Typography variant="subtitle2" letterSpacing={1} fontStyle="normal" color={colors.blueGrey[700]} fontWeight="bold">
                                            {group.name}
                                        </Typography>
                                    </GroupHeaderCell>
                                </TableRow>
                                {group.metrics.map((metric) => (
                                    <TableRow key={`${group.name}-${metric}`} hover sx={{ height: '27px' }}>
                                        <StickyCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {metric}
                                            </Typography>
                                        </StickyCell>
                                        {dates.map((date) => {
                                            const metricData = data[group.name]?.[metric]?.[date];
                                            const value = metricData?.value;
                                            const isMonetary = metricData?.monetory || false;

                                            return (
                                                <TableCell key={`${group.name}-${metric}-${date}`} align="right">
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                        <span>{formatValue(value, isMonetary)}</span>
                                                    </div>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        </>
    );
};

export default FinancialTable;